import type { FlowTriggers, VariableType } from "@velizhanin-dev/bot-scenario"
import { apiClient } from "@/shared/api/client"
import type { Revised } from "./keys"
import { scenarioPath } from "./keys"
import type {
	CreateNodeInput,
	DraftEdge,
	DraftFlow,
	DraftNode,
	DraftVariable,
	NodePosition,
} from "./types"

// --- правки черновика (все, кроме раскладки, с baseRevision) ---

export const createFlow = async (id: string, body: { baseRevision: number; name: string }) =>
	(
		await apiClient.post<Revised<{ flow: DraftFlow; nodes: DraftNode[] }>>(
			scenarioPath(id, "/flows"),
			body,
		)
	).data

export const duplicateFlow = async (id: string, flowId: string, body: { baseRevision: number }) =>
	(
		await apiClient.post<
			Revised<{ flow: DraftFlow; nodes: DraftNode[]; edges: DraftEdge[] }>
		>(scenarioPath(id, `/flows/${flowId}/duplicate`), body)
	).data

export const updateFlow = async (
	id: string,
	flowId: string,
	body: {
		baseRevision: number
		name?: string
		sortOrder?: number
		triggers?: FlowTriggers | null
		active?: boolean
		main?: true
	},
) =>
	(
		await apiClient.patch<Revised<{ flow: DraftFlow; previousMain?: DraftFlow }>>(
			scenarioPath(id, `/flows/${flowId}`),
			body,
		)
	).data

export const deleteFlow = async (id: string, flowId: string, baseRevision: number) =>
	(
		await apiClient.delete<Revised<{ flowId: string }>>(scenarioPath(id, `/flows/${flowId}`), {
			params: { baseRevision },
		})
	).data

export const createNode = async (id: string, body: CreateNodeInput & { baseRevision: number }) =>
	(
		await apiClient.post<Revised<{ node: DraftNode; edge: DraftEdge | null }>>(
			scenarioPath(id, "/nodes"),
			body,
		)
	).data

export const updateNode = async (
	id: string,
	nodeId: string,
	body: { baseRevision: number; config?: Record<string, unknown>; note?: string | null },
) =>
	(
		await apiClient.patch<Revised<{ node: DraftNode; removedEdgeIds: string[] }>>(
			scenarioPath(id, `/nodes/${nodeId}`),
			body,
		)
	).data

export const copyNode = async (id: string, nodeId: string, body: { baseRevision: number }) =>
	(
		await apiClient.post<Revised<{ node: DraftNode }>>(
			scenarioPath(id, `/nodes/${nodeId}/copy`),
			body,
		)
	).data

export const moveNode = async (
	id: string,
	nodeId: string,
	body: { baseRevision: number; flowId: string },
) =>
	(
		await apiClient.post<Revised<{ node: DraftNode; removedEdgeIds: string[] }>>(
			scenarioPath(id, `/nodes/${nodeId}/move`),
			body,
		)
	).data

export const deleteNode = async (id: string, nodeId: string, baseRevision: number) =>
	(
		await apiClient.delete<Revised<{ nodeId: string; removedEdgeIds: string[] }>>(
			scenarioPath(id, `/nodes/${nodeId}`),
			{ params: { baseRevision } },
		)
	).data

export const savePositions = async (id: string, positions: NodePosition[]) =>
	(await apiClient.put<{ updated: number }>(scenarioPath(id, "/positions"), { positions })).data

export const putEdge = async (
	id: string,
	body: { baseRevision: number; sourceNodeId: string; sourcePort: string; targetNodeId: string },
) => (await apiClient.put<Revised<{ edge: DraftEdge }>>(scenarioPath(id, "/edges"), body)).data

export const deleteEdge = async (id: string, edgeId: string, baseRevision: number) =>
	(
		await apiClient.delete<Revised<{ edgeId: string }>>(scenarioPath(id, `/edges/${edgeId}`), {
			params: { baseRevision },
		})
	).data

export const createVariable = async (
	id: string,
	body: {
		baseRevision: number
		name: string
		type: VariableType
		defaultValue?: string | null
		description?: string | null
	},
) =>
	(await apiClient.post<Revised<{ variable: DraftVariable }>>(scenarioPath(id, "/variables"), body))
		.data

export const updateVariable = async (
	id: string,
	variableId: string,
	body: {
		baseRevision: number
		type?: VariableType
		defaultValue?: string | null
		description?: string | null
	},
) =>
	(
		await apiClient.patch<Revised<{ variable: DraftVariable }>>(
			scenarioPath(id, `/variables/${variableId}`),
			body,
		)
	).data

export const deleteVariable = async (id: string, variableId: string, baseRevision: number) =>
	(
		await apiClient.delete<Revised<{ variableId: string }>>(
			scenarioPath(id, `/variables/${variableId}`),
			{ params: { baseRevision } },
		)
	).data
